import { Node } from './node';
import { Edge } from './edge';
import { Graph } from './graph';

/**
 * The class Grid is responsible for managing the canvas,
 * on which the graph or tree is drawn. 
 * The grid class divides the canvas into a grid, 
 * which cells serves as potential node container. 
 * To draw on the canvas, other classes need to call up the 
 * services provided by the grid class, which ensure 
 * that it's component will be scaled and drawn correctly.
 */
export class Grid {
    protected rows: number; //number of rows
    protected cols: number; //number of columns
    protected canvas: HTMLCanvasElement;//reference to the canvas
    protected graph: Graph;//Reference to the graph drawn on the grid
    protected gridVisible: boolean = false;//Visibility of the grid.


    //Unimportant
    private readonly screenRatio: number;

    constructor(rows: number, cols: number, canvas: HTMLCanvasElement) {
        this.rows = rows;
        this.cols = cols;
        this.canvas = canvas;

        if (window != null) {
            this.screenRatio = (screen.width / screen.height) / 3;
            this.screenRatio = 1;
            this.canvas.width = this.getCanvasWidth();
            this.canvas.height = this.getCanvasHeight();
        }

    }

    onResize() {
        this.canvas.width = this.getCanvasWidth();
        this.canvas.height = this.getCanvasHeight();
    }

    getCanvasWidth(): number {
        return this.canvas.clientWidth;
    }

    getCanvasHeight(): number {
        return (this.canvas.clientWidth / this.cols) * this.rows;
    }

    /**
     * 
     * @param xRaw raw-coordinates on the x-axis of the canvas.
     * @param yRaw raw-coordinates on the y-axis of the canvas.
     * 
     * @returns coordinates (rounded to whole numbers!) on the grid [row,col] as tuple, row can be adressed by result[0], col by result[1]
     *         If the rawcoordinates are not within the canvas we return [0,0]
     */
    getCellCoordinates(xRaw: number, yRaw: number): [number, number] {
        let rowIndex: number = Math.floor(yRaw / this.getHeightCell());
        let colIndex: number = Math.floor(xRaw / this.getWidthCell()); //We want a whole Number as Index
       
        if(rowIndex < 0){
            rowIndex = 0;
        }
        if(rowIndex > this.rows-1){
            rowIndex = this.rows-1;
        }
        if(colIndex < 0){
            colIndex = 0;
        }
        if(colIndex > this.cols-1){
            colIndex = this.cols-1;
        }

        return [rowIndex, colIndex];
    }

    /**
     *@param xRaw raw-coordinates on the x-axis of the canvas.
     *@param yRaw raw-coordinates on the y-axis of the canvas. 
     *
     *@returns coordinates (not rounded to whole numbers!!)  on the grid [row,col] as tuple, row can be adressed by result[0], col by result[1]
     *         If the rawcoordinates are not within the canvas we return [-1,-1]
     *  */
    getRawCellCoordinates(xRaw: number, yRaw: number): [number, number] {
        let rowIndex: number = (yRaw / this.getHeightCell());
        let colIndex: number = (xRaw / this.getWidthCell());

        if(rowIndex < 0){
            rowIndex = 0;
        }
        if(rowIndex > this.rows){
            rowIndex = this.rows;
        }
        if(colIndex < 0){
            colIndex = 0;
        }
        if(colIndex > this.cols){
            colIndex = this.cols;
        }

        return [rowIndex, colIndex];

    }

    convertCoordinateToRaw(row: number, col: number): [number, number] {
        return [col * this.getWidthCell(),
        row * this.getHeightCell()];
    }



    getWidthCell(): number {
        return this.getWidthCanvas() / this.cols;
    }

    getHeightCell(): number {
        return this.getHeightCanvas() / this.rows;
    }

    getWidthCanvas(): number {
        return this.getCanvasWidth();
    }

    getHeightCanvas(): number {
        return this.getCanvasHeight();
    }

    getWidthRadius(): number {
        return this.getWidthCell() / 3;
    }

    getHeightRadius(): number {
        return this.getHeightCell() / 3;
    }


    getBezierRadius(): number {
        return Math.min(this.getWidthRadius(), this.getHeightRadius()) / 2;
    }
    /**
    * Returns the center of the cell at [rowindex,colindex] 
    */
    getCellCenter(rowIndex: number, colIndex: number): [number, number] {
        if (rowIndex < 0 || rowIndex > this.rows - 1 || colIndex < 0 || colIndex > this.cols - 1) {
            return [0, 0];
        }
        return [colIndex * this.getWidthCell() + this.getWidthCell() / 2,
        rowIndex * this.getHeightCell() + this.getHeightCell() / 2];
    }

    getUpperLeftCorner(rowIndex: number, colIndex: number): [number, number] {
        if (rowIndex < 0 || rowIndex > this.rows - 1 || colIndex < 0 || colIndex > this.cols - 1) {
            return [0, 0];
        }

        return [colIndex * this.getWidthCell(), rowIndex * this.getHeightCell()];
    }

    getUpperMid(rowIndex: number, colIndex:number): [number,number]{
        let upperLeft: [number,number] = this.getUpperLeftCorner(rowIndex, colIndex);
        return [upperLeft[0] + this.getWidthCell()/2, upperLeft[1]]
    }

    getLowerMid(rowIndex: number, colIndex: number): [number,number]{
        let upperLeft: [number,number] = this.getUpperLeftCorner(rowIndex, colIndex);
        return [upperLeft[0] + this.getWidthCell()/2, upperLeft[1]+this.getHeightCell()]
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.gridVisible) {
            this.drawGrid(ctx);
        }
    }

    private drawGrid(ctx: CanvasRenderingContext2D) {
        var i: number = 0;

        for (i = 0; i < this.cols; i++) {
            ctx.fillStyle = 'black';
            ctx.beginPath();
            ctx.moveTo(this.getWidthCell() * i, 0);
            ctx.lineTo(this.getWidthCell() * i, this.getHeightCanvas());
            ctx.stroke();
            ctx.closePath();
        }

        for (i = 0; i < this.rows; i++) {
            ctx.beginPath();
            ctx.moveTo(0, this.getHeightCell() * i);
            ctx.lineTo(this.getWidthCanvas(), this.getHeightCell() * i);
            ctx.stroke();
            ctx.closePath();
        }
    }
    setVisible(visible: boolean) {
        this.gridVisible = visible;
    }







}
